import { defineStore } from 'pinia'
import { Generations, PaymentsToSendEvent, PaymentToSendEvent, UserAuth } from '~/static/types/user'
import { PaymentStatus } from '~/static/enums/payment-status'
import { PurchaseType, Purchases } from '~/static/enums/purchases'
import { hasGenerationsStorage, SubscriptionDuration, SubscriptionTariff } from '~/static/enums/user/subscription'

interface AuthState {
  user: UserAuth | null
  paymentToSendEvent: PaymentsToSendEvent
  cuncelSubscribtionText: string
  cuncelSubscribtionTextError: string
  checkPaymentTimer: NodeJS.Timeout | null
  startTime: number | null
}

interface CreatePaymentResponse {
  new_payment: true
  pay_url: string | false
}
interface UpgradeSubscriptionResponse {
  new_payment: true
  paid: boolean
  pay_url: string | false
}

export const useAuth = defineStore('auth', {
  state: (): AuthState => {
    return {
      user: null,
      paymentToSendEvent: {
        confirmed: [],
        pending: null,
      },
      cuncelSubscribtionText: '',
      cuncelSubscribtionTextError: '',
      checkPaymentTimer: null,
      startTime: null,
    }
  },
  actions: {
    async login(token: string) {
      const cookieToken = useCookie('token', {
        path: '/',
        expires: new Date(Date.now() + 10 * 365 * 24 * 3600 * 1000),
      })
      cookieToken.value = token
      nextTick(() => {
        this.getUser()
      })
      if (useRoute().path === '/') {
        useRouter().push('/files')
      }
    },
    async getUser() {
      interface GetUserResponse {
        user: UserAuth
        payment_to_send_event: PaymentsToSendEvent
      }
      const { response, error } = await useApi<GetUserResponse>('auth.getUser')
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        const cookieToken = useCookie('token')
        cookieToken.value = null
      } else {
        this.user = response.user
        this.paymentToSendEvent = response.payment_to_send_event
        if (!process.server) {
          const ati = useCookie('amplitude_track_id')
          if (this.user.ati !== null && this.user.ati !== undefined) {
            ati.value = this.user.ati
          } else {
            this.user.ati = <string>ati.value
          }
        }
        if (typeof window !== 'undefined') {
          if (this.paymentToSendEvent.confirmed.length > 0) {
            this.handlePaymentEvents(this.paymentToSendEvent.confirmed)
          }
          if (this.paymentToSendEvent.pending !== null) {
            console.error('Получен пользователь с неподтвержденным платежем.', {
              extra: {
                payment_id: this.paymentToSendEvent.pending.id,
              },
            })
            this.startCheckPayment()
          }
        }
      }
    },
    logout() {
      this.user = null
      const token = useCookie('token')
      token.value = null
      if (useRoute().path === '/cards' || useRoute().path === '/templates' || useRoute().path === '/files') {
        useRouter().push('/')
      }
    },
    async markDownload() {
      if (this.user === null || (!this.user.subscription && this.user.remaining_downloads === 0)) {
        return
      }
      const { response, error } = await useApi<boolean>('card.markDownload', {}, HttpMethod.POST)
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response) {
        if (!this.user.subscription) {
          this.user.remaining_downloads--
        }
        return true
      }
    },
    async createSubscription(
      tariff: SubscriptionTariff,
      duration: SubscriptionDuration,
    ): Promise<string | undefined> {
      if (!this.user || this.user.subscription) {
        return
      }

      interface SubscriptionExistsResponse {
        new_payment: false
      }
      const { response, error } = await useApi<CreatePaymentResponse | SubscriptionExistsResponse>(
        'subscription.buy',
        {},
        HttpMethod.POST,
        { tariff, duration },
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response.new_payment && response.pay_url) {
        return response.pay_url
      } else {
        this.getUser()
      }
      return undefined
    },
    async buyUpgradeSubscription(tariff: SubscriptionTariff): Promise<string | true | undefined> {
      if (!this.user?.subscription || this.user.subscription.tariff >= tariff) {
        this.getUser()
        return
      }

      interface CannotUpgradeResponse {
        new_payment: false
      }
      const { response, error } = await useApi<UpgradeSubscriptionResponse | CannotUpgradeResponse>(
        'subscription.upgrade',
        { tariff },
        HttpMethod.POST,
      )
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
      } else if (response.new_payment) {
        if (!response.paid && response.pay_url) {
          return response.pay_url
        } else {
          await this.getUser()
          return new Promise((resolve) => {
            const interval = setInterval(() => {
              if (this.paymentToSendEvent.pending === null) {
                clearInterval(interval)
                resolve(true)
              }
            }, 500)
          })
        }
      } else {
        this.getUser()
      }
      return undefined
    },
    async cuncelSubscription() {
      if (!this.user?.subscription) {
        return
      }

      this.cuncelSubscribtionTextError = new Validator(this.cuncelSubscribtionText)
        .required('Даже короткий отзыв будет полезен')
        .allErrorsToString()

      if (this.cuncelSubscribtionTextError !== '') {
        return false
      }

      const { error } = await useApi<true>('subscription.cancel', {}, HttpMethod.POST, {
        text: this.cuncelSubscribtionText,
      })
      if (error) {
        // eslint-disable-next-line no-console
        console.log(error.error_code, error.error_msg)
        return false
      } else {
        await this.getUser()
        return true
      }
    },
    initCheckPayment() {
      if (this.user === null) {
        return
      }
      this.paymentToSendEvent.pending = {
        id: null,
        type: PurchaseType.MonthSubscription,
        date: '',
      }
      this.startCheckPayment()
    },
    async handlePaymentEvents(payments: PaymentToSendEvent[]) {
      for (const payment of payments) {
        await this.sendPaymentEvent(payment).then(async () => {
          await this.markEventIsSent((payment.id as number))
        })
      }
    },
    subGeneration() {
      if (this.user) {
        if (!this.user.generations) {
          const now = new Date()
          now.setDate(now.getDate() + 30)
          this.user.generations = {
            remaining_count: 5,
            total_count: 5,
            expired_at: now.toDateString(),
          }
        }
        if (this.user.generations.remaining_count) {
          this.user.generations.remaining_count--
        }
      }
    },
    addGeneration() {
      if (this.user?.generations?.remaining_count) {
        this.user.generations.remaining_count++
      }
    },
    setGenerations(generations: Generations) {
      if (this.user) {
        this.user.generations = generations
      }
    },
    async sendPaymentEvent(payment: PaymentToSendEvent) {
      const ym = (<{ ym?: unknown }>window).ym
      if (typeof ym === 'function') {
        ym(useRuntimeConfig().public.ymKey, 'reachGoal', Purchases[payment.type].ymEventName, {
          id: payment.id,
          date: payment.date,
        })
      }
    },
    async markEventIsSent(id: number) {
      await useApi<true>('payment.markEventIsSent', {}, HttpMethod.POST, {
        id,
      })
      console.error('payment-' + id, {
        extra: {
          message: 'Платеж отмечен как отправленный в метрику и амплитуду.',
        },
      })
    },
    async checkPayment() {
      if (this.paymentToSendEvent.pending === null) {
        this.stopCheckPayment()
        return
      }

      interface CheckPaymentResponse {
        status: PaymentStatus
        payment?: PaymentToSendEvent
      }

      const { response } = await useApi<CheckPaymentResponse>('payment.check', {
        id: this.paymentToSendEvent.pending.id,
      })
      if (response) {
        if (response.status === PaymentStatus.Confirmed) {
          localStorage.setItem('reset-user', 'true')
          console.log('payment-' + this.paymentToSendEvent.pending.id, {
            extra: {
              message: 'Начата отправка событий для платежа подтвержденного только что.',
            },
          })
          await this.sendPaymentEvent(response.payment as PaymentToSendEvent).then(async () => {
            await this.markEventIsSent(<number>(response.payment as PaymentToSendEvent).id)
          })
          this.paymentToSendEvent.pending = null
          this.stopCheckPayment()
        } else if (response.status !== PaymentStatus.Pending) {
          this.paymentToSendEvent.pending = null
          this.stopCheckPayment()
        } else if (((new Date().getTime()) - <number>this.startTime) > 10 * 60 * 1000) {
          this.stopCheckPayment()
        }
      } else {
        this.stopCheckPayment()
      }
    },
    async startCheckPayment() {
      if (this.user === null) {
        return
      }
      if (this.paymentToSendEvent.pending === null) {
        return
      }
      if (this.checkPaymentTimer !== null) {
        clearInterval(this.checkPaymentTimer)
      }
      this.startTime = (new Date()).getTime()
      this.checkPaymentTimer = setInterval(this.checkPayment, 3000)
    },
    stopCheckPayment() {
      clearInterval(<NodeJS.Timeout>this.checkPaymentTimer)
      this.checkPaymentTimer = null
    },
  },
  getters: {
    isAuth(state) {
      return state.user !== null && state.user !== undefined
    },
    isAdmin(state) {
      return state.user?.email === useRuntimeConfig().public.adminEmail
    },
    hasGenerationsStorage(state) {
      return state.user?.subscription && hasGenerationsStorage(state.user.subscription.tariff)
    },
  },
})
